import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip,
  CircularProgress, Alert, TextField, MenuItem, Select, FormControl,
  InputLabel, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText, Divider, Grid, Card, CardContent, Rating,
  IconButton, Drawer,
} from '@mui/material';
import {
  Close, CheckCircle, Cancel, Pending, OpenInNew,
} from '@mui/icons-material';
import { getRequest, patchRequest } from '../../services/api';
import moment from 'moment';

// ─── helpers ──────────────────────────────────────────────────────────────────

const statusColor = { verified: 'success', unverified: 'warning', pending: 'warning', rejected: 'error' };

function InfoRow({ label, value }) {
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 'bold', width: 180, border: '1px solid #e0e0e0' }}>{label}</TableCell>
      <TableCell sx={{ border: '1px solid #e0e0e0' }}>{value || '—'}</TableCell>
    </TableRow>
  );
}

function DocImage({ label, src }) {
  if (!src) return null;
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" display="block" fontWeight="bold" sx={{ mb: 0.5 }}>{label}</Typography>
      <Box
        component="img"
        src={src}
        alt={label}
        onClick={() => window.open(src, '_blank')}
        sx={{
          width: 200, height: 130, objectFit: 'cover', borderRadius: 1,
          border: '2px solid #e0e0e0', cursor: 'pointer',
          transition: 'transform .15s',
          '&:hover': { transform: 'scale(1.03)' },
        }}
      />
    </Box>
  );
}

// ─── TAB: SERVICES ────────────────────────────────────────────────────────────
function ServicesTab({ partnerId }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getRequest(`services?filter=${JSON.stringify({ where: { partnerId } })}`)
      .then(d => setServices(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [partnerId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
        Total Services: <strong>{services.length}</strong>
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f4f6f8' }}>
              {['Service Name','Type','Category','Price','Discount','City','Range','Rating'].map(h =>
                <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {services.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">
                <Typography color="text.secondary" sx={{ py: 2 }}>No services found</Typography>
              </TableCell></TableRow>
            ) : services.map(s => (
              <TableRow key={s.serviceId} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#f9fafb' } }}>
                <TableCell>{s.name}</TableCell>
                <TableCell><Chip label={s.serviceType} size="small" color={s.serviceType === 'monthly' ? 'secondary' : 'info'} variant="outlined" /></TableCell>
                <TableCell>{s.categoryName}</TableCell>
                <TableCell>₹{s.totalCost}</TableCell>
                <TableCell>{s.discountPercentage}%</TableCell>
                <TableCell>{s.city}</TableCell>
                <TableCell>{s.range} km</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Rating value={Math.min(Number(s.rating) || 0, 5)} readOnly precision={0.1} size="small" />
                    <Typography variant="caption">({Number(s.rating || 0).toFixed(1)})</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── TAB: SERVICE HISTORY ─────────────────────────────────────────────────────
function ServiceHistoryTab({ partnerId }) {
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [rpp, setRpp]             = useState(10);
  const [status, setStatus]       = useState('upcoming');
  const [filterType, setFilter]   = useState('all');
  const [specificDate, setDate]   = useState('');
  const [totalEarning, setEarn]   = useState(0);
  const [loading, setLoading]     = useState(false);

  const statusOptions = ['upcoming', 'ongoing', 'completed', 'cancelled'];
  const durationOptions = [
    { label: 'All Bookings', value: 'all' },
    { label: 'Today',        value: 'today' },
    { label: 'This Week',    value: 'week' },
    { label: 'This Month',   value: 'month' },
    { label: 'Specific Date',value: 'specific' },
  ];

  useEffect(() => { load(); }, [page, rpp, status, filterType, specificDate]);

  const load = async () => {
    setLoading(true);
    try {
      let dateWhere = '';
      const fmt = d => d.toISOString();
      const now = new Date();
      if (filterType === 'today') {
        const s = new Date(now); s.setHours(0,0,0,0);
        dateWhere = `,"serviceDate":{"gte":"${fmt(s)}"}`;
      } else if (filterType === 'week') {
        const s = new Date(now); s.setDate(s.getDate() - s.getDay()); s.setHours(0,0,0,0);
        dateWhere = `,"serviceDate":{"gte":"${fmt(s)}"}`;
      } else if (filterType === 'month') {
        const s = new Date(now.getFullYear(), now.getMonth(), 1);
        dateWhere = `,"serviceDate":{"gte":"${fmt(s)}"}`;
      } else if (filterType === 'specific' && specificDate) {
        const s = new Date(specificDate); s.setHours(0,0,0,0);
        const e = new Date(specificDate); e.setHours(23,59,59,999);
        dateWhere = `,"serviceDate":{"gte":"${fmt(s)}","lte":"${fmt(e)}"}`;
      }

      const [data, cnt] = await Promise.all([
        getRequest(`service-requests?filter={"where":{"partnerId":"${partnerId}","status":"${status}"${dateWhere}},"skip":${page * rpp},"limit":${rpp},"order":"createdAt desc","include":[{"relation":"service"}]}`),
        getRequest(`service-requests/count?where={"partnerId":"${partnerId}","status":"${status}"${dateWhere}}`),
      ]);
      setRows(data || []);
      setTotal(cnt?.count || 0);
      setEarn((data || []).reduce((s, r) => s + ((Number(r.totalAmount) * 85) / 100), 0));
    } catch (e) {} finally { setLoading(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField select label="Duration" value={filterType} onChange={e => { setFilter(e.target.value); setPage(0); }} size="small" sx={{ minWidth: 160 }}>
          {durationOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        {filterType === 'specific' && (
          <TextField type="date" label="Date" InputLabelProps={{ shrink: true }} value={specificDate}
            onChange={e => setDate(e.target.value)} size="small" />
        )}
        <TextField select label="Status" value={status} onChange={e => { setStatus(e.target.value); setPage(0); }} size="small" sx={{ minWidth: 140 }}>
          {statusOptions.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
        </TextField>
      </Box>
      <Typography variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
        Total: <strong>{total}</strong> &nbsp;|&nbsp; Total Earning: <strong>₹{totalEarning.toFixed(2)}</strong>
      </Typography>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f4f6f8' }}>
                {['Customer','Mobile','Sub Category','Service Type','Category','Booking ID','Address','Pincode','Date','Earning'].map(h =>
                  <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={10} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={10} align="center"><Typography color="text.secondary" sx={{ py: 2 }}>No records</Typography></TableCell></TableRow>
              : rows.map(r => (
                <TableRow key={r.serviceRequestId} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#f9fafb' } }}>
                  <TableCell>{r.customerName}</TableCell>
                  <TableCell>{r.mobileNumber}</TableCell>
                  <TableCell>{r.service?.subCategoryName || '—'}</TableCell>
                  <TableCell><Chip label={r.service?.serviceType === 'monthly' ? 'Contract' : 'Daily'} size="small" variant="outlined" color={r.service?.serviceType === 'monthly' ? 'secondary' : 'info'} /></TableCell>
                  <TableCell>{r.service?.categoryName || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.bookingId || '—'}</TableCell>
                  <TableCell noWrap>{r.address}</TableCell>
                  <TableCell>{r.pincode}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.serviceDate ? moment(r.serviceDate).format('DD MMM YY') : '—'}</TableCell>
                  <TableCell><strong>₹{((Number(r.totalAmount) * 85) / 100).toFixed(2)}</strong></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} rowsPerPage={rpp}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRpp(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]} />
      </Paper>
    </Box>
  );
}

// ─── TAB: PAYMENT HISTORY (Contracts) ────────────────────────────────────────
function PaymentHistoryTab({ partnerId }) {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [rpp, setRpp]         = useState(10);
  const [earning, setEarn]    = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, [page, rpp]);

  const computeEarning = (data) => {
    const sum = (data || []).reduce((acc, item) => {
      const price = parseInt(item?.amount) || 0;
      if (price <= 10000)       return acc + (price - 100);
      if (price < 100000)       return acc + (price * 95) / 100;
      return acc + (price - 5000);
    }, 0);
    setEarn(sum);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [data, cnt] = await Promise.all([
        getRequest(`contracts?filter={"where":{"partnerId":"${partnerId}"},"skip":${page * rpp},"limit":${rpp},"order":"createdAt desc","include":[{"relation":"serviceRequest"}]}`),
        getRequest(`contracts/count?where={"partnerId":"${partnerId}"}`),
      ]);
      setRows(data || []);
      setTotal(cnt?.count || 0);
      computeEarning(data);
    } catch (e) {} finally { setLoading(false); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
        Total Contracts: <strong>{total}</strong> &nbsp;|&nbsp; Total Earning: <strong>₹{earning.toFixed(2)}</strong>
      </Typography>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f4f6f8' }}>
                {['Customer','Mobile','Booking ID','Address','Pincode','Est. Days','Range','Amount','Your Earning'].map(h =>
                  <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={9} align="center"><Typography color="text.secondary" sx={{ py: 2 }}>No contracts</Typography></TableCell></TableRow>
              : rows.map((r, i) => {
                const price = parseInt(r.amount) || 0;
                const earn = price <= 10000 ? price - 100 : price < 100000 ? (price * 95) / 100 : price - 5000;
                return (
                  <TableRow key={r.contractId || i} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#f9fafb' } }}>
                    <TableCell>{r.serviceRequest?.customerName || '—'}</TableCell>
                    <TableCell>{r.serviceRequest?.mobileNumber || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.serviceRequest?.bookingId || '—'}</TableCell>
                    <TableCell>{r.serviceRequest?.address || '—'}</TableCell>
                    <TableCell>{r.serviceRequest?.pincode || '—'}</TableCell>
                    <TableCell>{r.estimatedDays || '—'}</TableCell>
                    <TableCell><strong>₹{r.range || '—'}</strong></TableCell>
                    <TableCell><strong>₹{r.amount || '—'}</strong></TableCell>
                    <TableCell><strong>₹{earn.toFixed(2)}</strong></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} rowsPerPage={rpp}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRpp(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]} />
      </Paper>
    </Box>
  );
}

// ─── TAB: PAST REQUESTS (requestlogs where this partner appears) ──────────────
function PastRequestsTab({ partnerId }) {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [rpp, setRpp]         = useState(10);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, [page, rpp]);

  const load = async () => {
    setLoading(true);
    try {
      const where = `{"or":[{"acceptedPartners":{"inq":["${partnerId}"]}},{"notAcceptedPartners":{"inq":["${partnerId}"]}}]}`;
      const [data, cnt] = await Promise.all([
        getRequest(`requestlogs?filter={"where":${where},"skip":${page * rpp},"limit":${rpp},"order":"requestedAt desc","include":[{"relation":"user"}]}`),
        getRequest(`requestlogs/count?where=${where}`),
      ]);
      setRows(data || []);
      setTotal(cnt?.count || 0);
    } catch (e) {} finally { setLoading(false); }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
        Total: <strong>{total}</strong>
      </Typography>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f4f6f8' }}>
                {['Customer','Mobile','City','Address','Service','This Partner','Requested At'].map(h =>
                  <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary" sx={{ py: 2 }}>No records</Typography></TableCell></TableRow>
              : rows.map((r, i) => {
                const accepted = (r.acceptedPartners || []).includes(partnerId);
                return (
                  <TableRow key={r.logId || i} hover sx={{ '&:nth-of-type(even)': { backgroundColor: '#f9fafb' } }}>
                    <TableCell>{r.user?.name || '—'}</TableCell>
                    <TableCell>{r.user?.mobileNumber || '—'}</TableCell>
                    <TableCell>{r.user?.city || '—'}</TableCell>
                    <TableCell>{r.user?.fullAddress || '—'}</TableCell>
                    <TableCell>{r.serviceName || '—'}</TableCell>
                    <TableCell>
                      {accepted
                        ? <Chip label="Accepted" color="success" size="small" />
                        : <Chip label="Not Accepted" color="error" size="small" />}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.requestedAt ? moment(r.requestedAt).format('DD MMM YY, HH:mm') : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} rowsPerPage={rpp}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRpp(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]} />
      </Paper>
    </Box>
  );
}

// ─── TAB: PROFILE + KYC ───────────────────────────────────────────────────────
function ProfileTab({ partner, onStatusChange }) {
  const [editOpen, setEditOpen]   = useState(false);
  const [form, setForm]           = useState({ ...partner });
  const [saving, setSaving]       = useState(false);
  const [success, setSuccess]     = useState('');
  const [error, setError]         = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await patchRequest(`users/${partner.userId}`, {
        name:         form.name,
        mobileNumber: form.mobileNumber,
        city:         form.city,
        status:       form.status,
        pincode:      form.pincode,
        fullAddress:  form.fullAddress,
      });
      setSuccess('Partner updated successfully');
      setEditOpen(false);
      onStatusChange && onStatusChange(form.status);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Failed to update partner');
    } finally { setSaving(false); }
  };

  return (
    <Box>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Info table */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Partner Details</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                <InfoRow label="Name"         value={partner.name} />
                <InfoRow label="Mobile"       value={partner.mobileNumber} />
                <InfoRow label="City"         value={partner.city} />
                <InfoRow label="Pincode"      value={partner.pincode} />
                <InfoRow label="Address"      value={partner.fullAddress} />
                <InfoRow label="Aadhaar No."  value={partner.aadhaarCardNumber} />
                <InfoRow label="PAN No."      value={partner.panCardNumber} />
                <InfoRow label="Wallet"       value={`₹${(partner.wallet || 0).toLocaleString('en-IN')}`} />
                <InfoRow label="Total Bookings" value={partner.totalBookings || 0} />
                <InfoRow label="Joined"       value={partner.createdAt ? moment(partner.createdAt).format('DD MMM YYYY') : '—'} />
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0' }}>Status</TableCell>
                  <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                    <Chip label={partner.status} color={statusColor[partner.status] || 'default'} size="small"
                      icon={partner.status === 'verified' ? <CheckCircle /> : <Pending />} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => setEditOpen(true)}>
            Edit Partner
          </Button>
        </Grid>

        {/* KYC documents */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>KYC Documents</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {partner.aadhaarCardUrl?.[0] && <DocImage label="Aadhaar Front" src={partner.aadhaarCardUrl[0]} />}
            {partner.aadhaarCardUrl?.[1] && <DocImage label="Aadhaar Back"  src={partner.aadhaarCardUrl[1]} />}
            {partner.panCardUrl          && <DocImage label="PAN Card"       src={partner.panCardUrl} />}
            {partner.panCardBackUrl      && <DocImage label="PAN Back"       src={partner.panCardBackUrl} />}
            {partner.userProfilePic      && <DocImage label="Profile Photo"  src={partner.userProfilePic} />}
            {!partner.aadhaarCardUrl && !partner.panCardUrl && !partner.userProfilePic && (
              <Typography color="text.secondary">No documents uploaded</Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Partner</DialogTitle>
        <DialogContent>
          {[
            { label: 'Name',         name: 'name' },
            { label: 'City',         name: 'city' },
            { label: 'Pincode',      name: 'pincode' },
            { label: 'Full Address', name: 'fullAddress' },
          ].map(f => (
            <TextField key={f.name} fullWidth margin="dense" label={f.label} name={f.name}
              value={form[f.name] || ''} onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))} />
          ))}
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select value={form.status || ''} label="Status" onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="unverified">Unverified</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── MAIN PARTNER PROFILE DRAWER ─────────────────────────────────────────────
const TABS = [
  { label: 'Services'        },
  { label: 'Service History' },
  { label: 'Payment History' },
  { label: 'Past Requests'   },
  { label: 'Profile & KYC'  },
];

const PartnerProfile = ({ partner, onClose, onStatusChange }) => {
  const [tab, setTab] = useState(0);

  if (!partner) return null;

  return (
    <Drawer
      anchor="right"
      open={Boolean(partner)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: '85%', lg: '78%' }, p: 0 } }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 3, py: 2, backgroundColor: 'primary.main', color: 'white',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {partner.name}
            {partner.uniqueId && (
              <Typography component="span" variant="body2" sx={{ ml: 1, opacity: 0.85 }}>
                #{partner.uniqueId}
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip label={partner.status} size="small"
              color={statusColor[partner.status] || 'default'}
              sx={{ fontSize: 11 }} />
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {partner.city} • {partner.pincode} • {partner.mobileNumber}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><Close /></IconButton>
      </Box>

      {/* Tabs */}
      <Paper square elevation={0} sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2 }}
        >
          {TABS.map((t, i) => <Tab key={i} label={t.label} sx={{ minHeight: 52 }} />)}
        </Tabs>
      </Paper>

      {/* Content */}
      <Box sx={{ p: 3, overflowY: 'auto' }}>
        {tab === 0 && <ServicesTab        partnerId={partner.userId} />}
        {tab === 1 && <ServiceHistoryTab  partnerId={partner.userId} />}
        {tab === 2 && <PaymentHistoryTab  partnerId={partner.userId} />}
        {tab === 3 && <PastRequestsTab    partnerId={partner.userId} />}
        {tab === 4 && <ProfileTab partner={partner} onStatusChange={onStatusChange} />}
      </Box>
    </Drawer>
  );
};

export default PartnerProfile;
